import { useEffect, useState, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useProfile } from './hooks/useGame';
import { useVisualViewport } from './hooks/useVisualViewport';
import { useAppStore } from './stores/appStore';
import { preloadSounds, startBackgroundMusic, stopBackgroundMusic, setMusicVolume, addUnlockedSongs, setBasePlaylist } from './services/audio';
import { fetchPuzzleList, fetchSystemStatus, fetchUnlockedSongs, fetchBasePlaylist, type SystemStatus } from './services/api';
import { useUserNotifications } from './hooks/useUserNotifications';
import { NotificationToast } from './components/NotificationToast';
import type { ToastNotification } from './components/NotificationToast';
import { UpdateModal } from './components/UpdateModal';
import { usePageTracking } from './hooks/usePageTracking';
// Lazy load game components for code splitting
const ClassicGame = lazy(() => import('./features/classic/ClassicGame'));
const FlipGame = lazy(() => import('./features/flip/FlipGame'));
const BridgeGame = lazy(() => import('./features/bridge/BridgeGame'));
const BlitzGame = lazy(() => import('./features/blitz/BlitzGame'));
const SwipeGame = lazy(() => import('./features/swipe/SwipeGame'));
const RotationsGame = lazy(() => import('./features/rotations/RotationsGame'));
const SplashScreen = lazy(() => import('./components/SplashScreen'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
import { Navbar } from './components/Navbar';
import { PuzzleLoading } from './components/PuzzleLoading';
import './App.css';

// Create a client with optimized settings for mobile performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Keep data fresh for 30 seconds to reduce refetches during puzzle switching
      staleTime: 30 * 1000,
      // Cache data for 5 minutes even when unused
      gcTime: 5 * 60 * 1000,
    },
  },
});

function AppContent() {
  usePageTracking();
  const { data: user, isLoading: isLoadingProfile } = useProfile();

  const {
    currentPuzzleId,
    setCurrentPuzzleId,
    currentMode,
    setCurrentMode,
    setPuzzles,
    gameState,
    isDarkMode,
    musicVolume
  } = useAppStore(
    useShallow(state => ({
      currentPuzzleId: state.currentPuzzleId,
      setCurrentPuzzleId: state.setCurrentPuzzleId,
      currentMode: state.currentMode,
      setCurrentMode: state.setCurrentMode,
      setPuzzles: state.setPuzzles,
      gameState: state.gameState,
      isDarkMode: state.isDarkMode,
      musicVolume: state.musicVolume
    }))
  );

  const [_isLoadingPuzzles, setIsLoadingPuzzles] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [toastNotifications, setToastNotifications] = useState<ToastNotification[]>([]);
  const location = useLocation();
  const isSplashRoute = location.pathname === '/';

  // Handle incoming notifications from SSE
  const handleNewNotification = useCallback((notification: ToastNotification) => {
    setToastNotifications(prev => [...prev, notification]);
  }, []);

  const handleDismissNotification = useCallback((id: string) => {
    setToastNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Connect to user notifications SSE
  useUserNotifications({ onNotification: handleNewNotification });

  // Check for system updates when entering game modes
  const [updateStatus, setUpdateStatus] = useState<SystemStatus | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    if (!isSplashRoute) {
      const checkUpdates = async () => {
        try {
          const status = await fetchSystemStatus();
          const savedVersion = localStorage.getItem('wordly_update_version');
          const lastSeen = savedVersion ? parseInt(savedVersion, 10) : 0;

          if (!isNaN(lastSeen) && status.version > lastSeen) {
            setUpdateStatus(status);
            setShowUpdateModal(true);
          }
        } catch (e) {
          // Silent fail
        }
      };
      // Small delay to ensure smooth transition
      const timer = setTimeout(checkUpdates, 1500);
      return () => clearTimeout(timer);
    }
  }, [isSplashRoute]);

  const handleDismissUpdate = () => {
    if (updateStatus) {
      localStorage.setItem('wordly_update_version', updateStatus.version.toString());
    }
    setShowUpdateModal(false);
  };

  // Set --keyboard-offset CSS variable for keyboard-aware positioning
  useVisualViewport();

  // Apply dark mode class to body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  // Get puzzle label from gameState
  const puzzleLabel = gameState?.puzzle?.label;

  // Preload sounds after first user interaction
  useEffect(() => {
    const handleInteraction = () => {
      preloadSounds();
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('click', handleInteraction);
    window.addEventListener('keydown', handleInteraction);

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, []);

  // Get music volume from store (consolidated above)


  // Background music: play on game routes, stop on splash
  // We use refs to track the previous route state so we only stop music when actually navigating to splash
  useEffect(() => {
    if (isSplashRoute) {
      // Always stop music on splash screen
      stopBackgroundMusic();
    } else {
      // On game routes, start music if volume > 0
      // startBackgroundMusic already handles "already playing" case internally
      if (musicVolume > 0) {
        startBackgroundMusic();
      }
    }

    // No cleanup function - we don't want to stop music on every re-render
    // Music only stops when navigating to splash or when user explicitly mutes
  }, [isSplashRoute, musicVolume]);

  // Sync music volume changes to the audio service
  useEffect(() => {
    setMusicVolume(musicVolume);
  }, [musicVolume]);

  // Fetch and add unlocked songs based on user's completed puzzles
  useEffect(() => {
    const loadUnlockedSongs = async () => {
      try {
        const response = await fetchUnlockedSongs();
        if (response.songs && response.songs.length > 0) {
          addUnlockedSongs(response.songs);
        }
      } catch (error) {
        // Silent fail - unlocked songs are not critical
        console.warn('Failed to fetch unlocked songs:', error);
      }
    };

    // Only fetch after user profile is loaded (to have proper auth context)
    if (!isLoadingProfile) {
      loadUnlockedSongs();
    }
  }, [isLoadingProfile]);

  // Fetch base playlist on mount (dynamic loading of dropped mp3s)
  useEffect(() => {
    const loadBasePlaylist = async () => {
      try {
        const response = await fetchBasePlaylist();
        if (response.songs && response.songs.length > 0) {
          setBasePlaylist(response.songs);
        }
      } catch (error) {
        // Silent fail - fallback to hardcoded list
        console.warn('Failed to fetch base playlist:', error);
      }
    };

    loadBasePlaylist();
  }, []);

  // Parse initial mode/puzzle from URL and fetch default puzzle
  // Respects persisted state from Zustand to prevent Android WebView reloads from resetting
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    const puzzleIdParam = params.get('puzzle_id');

    // Only override mode if explicitly provided in URL
    // Otherwise, currentMode persists from localStorage via Zustand
    if (modeParam && ['classic', 'flip', 'blitz', 'bridge', 'swipe', 'rotations'].includes(modeParam)) {
      const newMode = modeParam as 'classic' | 'flip' | 'blitz' | 'bridge' | 'swipe' | 'rotations';
      // If mode is changing, clear the puzzle ID to force fresh fetch for new mode
      if (newMode !== currentMode) {
        setCurrentPuzzleId(null);
      }
      setCurrentMode(newMode);
    }

    // Helper to check if puzzleId matches the expected mode
    const puzzleIdMatchesMode = (puzzleId: string | null, mode: string): boolean => {
      if (!puzzleId) return false;
      // Classic puzzles have IDs like 'puzzle_001', flip like 'flip_001', bridge like 'bridge_001'
      if (mode === 'classic') return !puzzleId.startsWith('flip_') && !puzzleId.startsWith('bridge_') && !puzzleId.startsWith('blitz_') && !puzzleId.startsWith('swipe_') && !puzzleId.startsWith('rotations_');
      return puzzleId.startsWith(`${mode}_`);
    };

    // Fetch puzzle list (always do this to populate the selector)
    const fetchPuzzles = async () => {
      setIsLoadingPuzzles(true);
      setInitError(null);
      try {
        // Use current mode (persisted or from URL) for API call
        const effectiveMode = modeParam || currentMode || 'classic';
        const response = await fetchPuzzleList(effectiveMode);

        // Store the puzzle list
        setPuzzles(response.puzzles || []);

        // Priority for puzzle ID:
        // 1. URL parameter (explicit navigation)
        // 2. Currently in a collab session — never override the collab puzzle ID
        // 3. Already have a persisted puzzleId that exists in the puzzle list AND matches the mode (Android resume)
        // 4. Server default
        // 5. Last puzzle in list
        const currentIsCollab = useAppStore.getState().isCollab;
        if (puzzleIdParam) {
          setCurrentPuzzleId(puzzleIdParam);
        } else if (currentIsCollab) {
          // In a live party session — keep whatever puzzle the collab set, don't reset
        } else if (currentPuzzleId && puzzleIdMatchesMode(currentPuzzleId, effectiveMode) && response.puzzles.some(p => p.id === currentPuzzleId)) {
          // Keep the persisted puzzle ID - don't reset (crucial for Android lifecycle)
          // No-op: currentPuzzleId is already set from persisted state
        } else if (response.default) {
          setCurrentPuzzleId(response.default);
        } else if (response.puzzles.length > 0) {
          // Fallback to last puzzle in list
          setCurrentPuzzleId(response.puzzles[response.puzzles.length - 1].id);
        }
      } catch (error: any) {
        console.error('Failed to fetch puzzles:', error);
        setInitError(error.message || 'Failed to connect to server');
      } finally {
        setIsLoadingPuzzles(false);
      }
    };

    // Skip fetching puzzles when on splash screen - wait until user navigates
    // Skip fetching puzzles when on splash screen - wait until user navigates
    // if (isSplashRoute) {
    //   return;
    // }

    fetchPuzzles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCurrentMode, setCurrentPuzzleId, setPuzzles, isSplashRoute, currentMode]);

  // Only block for initial profile load or critical errors - not puzzle loading
  // Game components handle their own mode-specific loading screens
  const hasInitError = !isSplashRoute && initError;
  const isBootstrapping = !isSplashRoute && isLoadingProfile;

  if (hasInitError) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>Loading Hot or Cold?...</p>
        <div style={{ color: '#ff6b6b', marginTop: '1rem', padding: '0 2rem', textAlign: 'center' }}>
          <p>Error: {initError}</p>
          <p style={{ fontSize: '0.8em', opacity: 0.8, marginTop: '0.5rem' }}>
            Ensure backend is running at 10.0.2.2:5000
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: 'var(--primary-color)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isBootstrapping) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>Loading Hot or Cold?...</p>
      </div>
    );
  }

  return (
    <div className="app">
      {!isSplashRoute && <Navbar user={user || null} puzzleLabel={puzzleLabel} isComplete={gameState?.game_over} />}

      {/* Global Notification Toasts */}
      <NotificationToast
        notifications={toastNotifications}
        onDismiss={handleDismissNotification}
      />

      {updateStatus && (
        <UpdateModal
          isOpen={showUpdateModal}
          onClose={handleDismissUpdate}
          title={updateStatus.title}
          message={updateStatus.message}
          date={updateStatus.date}
        />
      )}

      <main
        className={isSplashRoute ? '' : `app-main mode-${currentMode || 'classic'}`}
        style={isSplashRoute ? { flex: 1 } : undefined}
      >
        <Routes>
          <Route path="/" element={
            <Suspense fallback={<div className="app-loading"><div className="app-loading-spinner" /></div>}>
              <SplashScreen />
            </Suspense>
          } />
          <Route path="/play" element={<GameRouter />} />
          <Route path="/play/classic" element={<ClassicGame />} />
          <Route path="/reset-password" element={
            <Suspense fallback={<div className="app-loading"><div className="app-loading-spinner" /></div>}>
              <ResetPassword />
            </Suspense>
          } />
          {/* Add more routes as modes are implemented */}
          {/* <Route path="/play/flip" element={<FlipGame />} /> */}
          {/* <Route path="/play/blitz" element={<BlitzGame />} /> */}
          {/* <Route path="/play/bridge" element={<BridgeGame />} /> */}
        </Routes>
      </main>
    </div>
  );
}

// Routes to the correct game mode based on store state
function GameRouter() {
  const { storeMode, puzzleId } = useAppStore(
    useShallow(state => ({
      storeMode: state.currentMode,
      puzzleId: state.currentPuzzleId
    }))
  );

  // Read mode from URL for immediate correct loading screen (before state updates)
  const urlParams = new URLSearchParams(window.location.search);
  const urlMode = urlParams.get('mode');
  const effectiveMode = (urlMode && ['classic', 'flip', 'blitz', 'bridge', 'swipe', 'rotations'].includes(urlMode))
    ? urlMode as 'classic' | 'flip' | 'blitz' | 'bridge' | 'swipe' | 'rotations'
    : storeMode;

  // Mode-aware loading fallback using effective mode
  const getLoadingMode = (): 'classic' | 'flip' | 'bridge' | 'swipe' => {
    if (effectiveMode === 'flip') return 'flip';
    if (effectiveMode === 'bridge') return 'bridge';
    if (effectiveMode === 'swipe') return 'swipe';
    return 'classic';
  };
  const loadingFallback = <PuzzleLoading mode={getLoadingMode()} message="Loading game..." />;

  const renderGame = () => {
    switch (effectiveMode) {
      case 'classic':
        return <ClassicGame key={puzzleId} />;
      case 'flip':
        return <FlipGame key={puzzleId} />;
      case 'blitz':
        return <BlitzGame key={puzzleId} />;
      case 'bridge':
        return <BridgeGame key={puzzleId} />;
      case 'swipe':
        return <SwipeGame key={puzzleId} />;
      case 'rotations':
        return <RotationsGame key={puzzleId} />;
      default:
        return <ClassicGame key={puzzleId} />;
    }
  };

  return (
    <Suspense fallback={loadingFallback}>
      {renderGame()}
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
